/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Select as AntSelect,
  notification,
} from 'antd';
import {
  SearchOutlined,
  ContainerOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import ModalConfirmation from 'components/ModalConfirmation';
import CRUDgeneric from 'utils/CRUDgeneric';
import { evaluarEmpleados } from 'api/configuraciones/clock/EvaluacionClock/EvaluacionGeneral';
import moment from 'moment';

const EvaluacionEmpleado = ({ permission, recordFather }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [numerosEmpleado, setNumerosEmpleado] = useState([]);

  const cliente = CRUDgeneric('/relojchecador/cargas-de-reportes-detalles-vista-optimized/');

  const fetchAll = async () => {
    setLoading(true);
    const res = await cliente.obtenerListaDeRegistros({
      mes_id: recordFather.mes.id,
      periodo_fiscal_id: recordFather.periodo_fiscal.id,
    });
    const merged = Array.isArray(res) ? res : (res?.results ?? []);
    setData(formatReceived(merged));
    setLoading(false);
  };

  const fetchData = async (nums) => {
    setLoading(true);
    const responses = await Promise.all(
      nums.map((num) => cliente.obtenerListaDeRegistros({
        numero_de_empleado: num,
        mes_id: recordFather.mes.id,
        periodo_fiscal_id: recordFather.periodo_fiscal.id,
      })),
    );
    const merged = responses.flatMap((res) => (Array.isArray(res) ? res : (res?.results ?? [])));
    setData(formatReceived(merged));
    setLoading(false);
  };

  useEffect(() => {
    if (recordFather?.mes?.id && recordFather?.periodo_fiscal?.id) {
      fetchAll();
    }
  // eslint-disable-next-line
  }, [recordFather]);

  const onBuscar = async () => {
    try {
      const nums = form.getFieldValue('numeros_de_empleado') || [];
      if (!nums.length) {
        await fetchAll();
        return;
      }
      setNumerosEmpleado(nums);
      await fetchData(nums);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };

  const onConfirmEvaluar = async () => {
    try {
      setLoading(true);
      const result = await evaluarEmpleados(
        [recordFather.mes.id],
        numerosEmpleado.map(String),
      );
      if (result) {
        const hasErrors = result.resultados?.some((r) => r.status === 'error');
        if (hasErrors) {
          const errDetails = result.resultados
            .filter((r) => r.status === 'error')
            .map((r) => r.detalle || `Mes ID ${r.mes_id}`)
            .join('; ');
          notification.warning({
            message: 'Evaluación con advertencias',
            description: `${result.empleados_procesados} empleado(s) procesado(s). Errores: ${errDetails}`,
            placement: 'topRight',
            duration: 0,
          });
        } else {
          notification.success({
            message: 'Evaluación completada',
            description: `Se procesaron ${result.empleados_procesados} empleado(s) correctamente.`,
            placement: 'topRight',
          });
        }
        if (numerosEmpleado.length) {
          await fetchData(numerosEmpleado);
        } else {
          await fetchAll();
        }
      }
      setVisibleConfirm(false);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
      setVisibleConfirm(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 100,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
      filterMode: 'contains',
    },
    {
      titleText: 'Día',
      dataIndex: 'dia_de_la_semana_nombre',
      key: 'dia_de_la_semana_nombre',
      width: 100,
      filterMode: 'contains',
    },
    {
      titleText: 'No. de Empleado',
      dataIndex: 'numero_de_empleado',
      key: 'numero_de_empleado',
      width: 150,
      filterMode: 'exact-first',
    },
    {
      titleText: 'Nombre del Empleado',
      dataIndex: 'numero_y_nombre_completo_empelado',
      key: 'numero_y_nombre_completo_empelado',
      width: 200,
      filterMode: 'contains',
    },
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area_clave_y_nombre',
      key: 'directorio_de_area_clave_y_nombre',
      width: 200,
      filterMode: 'contains',
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto_clave_y_descripcion',
      key: 'puesto_clave_y_descripcion',
      width: 190,
      filterMode: 'contains',
    },
    {
      titleText: 'Plaza',
      dataIndex: 'plaza_clave_y_descripcion',
      key: 'plaza_clave_y_descripcion',
      width: 200,
      filterMode: 'contains',
    },
    {
      titleText: 'Hora de Entrada',
      dataIndex: 'hora_entrada',
      key: 'hora_entrada',
      width: 150,
      filterMode: 'contains',
    },
    {
      titleText: 'Hora de Salida',
      dataIndex: 'hora_salida',
      key: 'hora_salida',
      width: 150,
      filterMode: 'contains',
    },
    {
      titleText: 'Evaluación de Entrada',
      dataIndex: 'tipo_de_evaluacion_entrada',
      key: 'tipo_de_evaluacion_entrada',
      width: 190,
      filterMode: 'contains',
    },
    {
      titleText: 'Evaluación de Salida',
      dataIndex: 'tipo_de_evaluacion_salida',
      key: 'tipo_de_evaluacion_salida',
      width: 180,
      filterMode: 'contains',
    },
    {
      titleText: 'Evaluación Otros',
      dataIndex: 'tipo_de_evaluacion_otro',
      key: 'tipo_de_evaluacion_otro',
      width: 160,
      filterMode: 'contains',
    },
  ];

  const mesNombre = recordFather?.mes?.nombre || '';
  const tieneRegistro = !!recordFather?.mes?.id;

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          name="evaluacion_empleado"
        >
          <Row gutter={10}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="numeros_de_empleado"
                label="No. de Empleado(s)"
              >
                <AntSelect
                  mode="tags"
                  allowClear
                  disabled={!tieneRegistro}
                  placeholder="Escriba un número y presione Enter para agregar"
                  tokenSeparators={[',', ' ']}
                  open={false}
                  onChange={(value) => {
                    if (!value || value.length === 0) {
                      setNumerosEmpleado([]);
                      fetchAll();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label=" ">
                <Button
                  icon={<SearchOutlined />}
                  onClick={onBuscar}
                  disabled={!tieneRegistro}
                >
                  Buscar
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {data !== null && (
          <Table
            cols={columns}
            data={data}
            scroll={{ y: 400 }}
            rowSelection={rowSelection}
            handleOnRowClick={(record) => setSelectedRowKeys([record.id])}
            permission={permission}
            allowExportPDF={false}
            autoFit={false}
            defaultSearchVisible
            titleTable={`Evaluación por Empleado — ${mesNombre}`}
            customActions={[
              {
                onClick: () => setVisibleConfirm(true),
                text: 'Evaluar Empleado(s)',
                icon: <ContainerOutlined />,
                disabledBySelection: false,
              },
            ]}
          />
        )}

        <ModalConfirmation
          onFinish={onConfirmEvaluar}
          ConfirmAutorize
          onCancel={() => setVisibleConfirm(false)}
          contentTitle={`Evaluar ${numerosEmpleado.length} empleado(s) — ${mesNombre}`}
          open={visibleConfirm}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default EvaluacionEmpleado;
